import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { userAPI, userCompanyAPI, bookingAPI, reviewAPI } from '../services/api';
import Layout from '../components/Layout';
import { StatCard } from '../components/ModernCard';
import Icon from '../components/Icon';
import { ONBOARDING_CALL_URL } from '../utils/externalLinks';
import { DEFAULT_APP_LOGO } from '../constants';
import '../styles/Dashboard.css';


// Check if user is within their first week
const isNewUser = (user) => {
  if (!user?.created_at) return false;
  const createdAt = new Date(user.created_at);
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return createdAt > oneWeekAgo;
};

function UserDashboard({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const navigate = useNavigate();
  const [bikes, setBikes] = useState([]);
  const [showOnboardingPrompt, setShowOnboardingPrompt] = useState(() => {
    // Check if user dismissed the onboarding prompt
    const dismissed = localStorage.getItem('dismissedOnboarding');
    return !dismissed && isNewUser(user);
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [userConnections, setUserConnections] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [showAllBikes, setShowAllBikes] = useState(false);
  const [showAllCompanies, setShowAllCompanies] = useState(false);
  const [highlightedBikeId, setHighlightedBikeId] = useState(null);
  const [selectedBikeFilter, setSelectedBikeFilter] = useState('');
  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const bikeRefs = useRef({});
  
  // Review notification state
  const [pendingReviews, setPendingReviews] = useState([]);
  const [dismissedReviews, setDismissedReviews] = useState(() => {
    const saved = localStorage.getItem('dismissedReviews');
    return saved ? JSON.parse(saved) : [];
  });
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewingService, setReviewingService] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  // Service verification (user not connected to company that serviced their bike)
  const [pendingServiceVerifications, setPendingServiceVerifications] = useState([]);
  const [verifyingServiceId, setVerifyingServiceId] = useState(null);

  // In-app notifications (e.g. bike transfer received)
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bikesRes, companiesRes, connectionsRes, bookingsRes, verificationsRes, notificationsRes] = await Promise.all([
        userAPI.getMyBikes(),
        userCompanyAPI.getAvailableCompanies(),
        userCompanyAPI.getUserConnections(),
        bookingAPI.getUserBookings().catch(() => ({ data: { bookings: [] } })),
        userAPI.getPendingServiceVerifications().catch(() => ({ data: { verifications: [] } })),
        userAPI.getNotifications({ limit: 10 }).catch(() => ({ data: { notifications: [] } }))
      ]);
      setBikes(bikesRes.data.bikes || []);
      setAvailableCompanies(companiesRes.data.companies || []);
      setUserConnections(connectionsRes.data.connections || []);
      
      // Get upcoming bookings (future or today)
      const today = new Date().toISOString().split('T')[0];
      const allBookings = bookingsRes.data.bookings || [];
      const upcoming = allBookings
        .filter(b => {
          const bookingDate = b.booking_date;
          return bookingDate >= today && (b.status === 'pending' || b.status === 'confirmed');
        })
        .sort((a, b) => {
          if (a.booking_date !== b.booking_date) {
            return a.booking_date.localeCompare(b.booking_date);
          }
          return (a.start_time || '').localeCompare(b.start_time || '');
        })
        .slice(0, 3); // Show next 3 upcoming
      setUpcomingBookings(upcoming);
      setPendingServiceVerifications(verificationsRes.data?.verifications || []);
      setNotifications(notificationsRes.data?.notifications || []);

      // Find services that need reviews (completed services without reviews)
      const bikesWithServices = bikesRes.data.bikes || [];
      const servicesNeedingReview = [];
      const reviewedCompanyIds = new Set(); // Track companies already reviewed by user
      const todayDate = new Date();
      todayDate.setHours(0, 0, 0, 0); // Reset time to start of day
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60); // Extended to 60 days
      sixtyDaysAgo.setHours(0, 0, 0, 0);
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1); // Don't show immediately, wait at least 1 day
      oneDayAgo.setHours(0, 0, 0, 0);
      
      for (const bike of bikesWithServices) {
        if (bike.services && bike.services.length > 0) {
          for (const service of bike.services) {
            // Only completed services (check various status formats)
            const status = (service.status || '').toLowerCase();
            if (status !== 'completed') {
              continue; // Skip non-completed services
            }
            
            // Skip if already dismissed
            if (dismissedReviews.includes(service.id)) {
              continue;
            }
            
            if (!service.service_date) {
              continue; // Skip services without date
            }
            
            const serviceDate = new Date(service.service_date);
            serviceDate.setHours(0, 0, 0, 0);
            
            // Service must be in the past (not future)
            if (serviceDate > todayDate) {
              continue; // Skip future-dated services
            }
            
            // Service must be at least 1 day old and within last 60 days
            if (serviceDate < oneDayAgo && serviceDate >= sixtyDaysAgo) {
              // Skip if user has already reviewed this company (only one review per company allowed)
              if (service.company_id && reviewedCompanyIds.has(service.company_id)) {
                continue;
              }
              
              // Check if service has review
              try {
                const reviewsResp = await reviewAPI.listForService(service.id);
                const reviews = reviewsResp.data.reviews || [];
                if (reviews.length === 0) {
                  // Also check company reviews to see if user already reviewed this company
                  if (service.company_id && !reviewedCompanyIds.has(service.company_id)) {
                    try {
                      const companyReviewsResp = await reviewAPI.listForCompany(service.company_id);
                      const companyReviews = companyReviewsResp.data.reviews || [];
                      // Check if current user has a review for this company
                      const userHasReviewedCompany = companyReviews.some(r => 
                        r.user_name === user?.firstname + ' ' + user?.surname ||
                        r.user_name === user?.username ||
                        r.user_id === user?.id
                      );
                      if (userHasReviewedCompany) {
                        reviewedCompanyIds.add(service.company_id);
                        continue;
                      }
                    } catch (e) {
                      // If we can't check, allow the review
                    }
                  }
                  
                  // Make sure we have company_name
                  const serviceWithReview = {
                    ...service,
                    bike: { id: bike.id, brand: bike.brand, model: bike.model },
                    company_name: service.company_name || 'Service Provider'
                  };
                  servicesNeedingReview.push(serviceWithReview);
                  // Mark this company as having a pending review prompt
                  if (service.company_id) {
                    reviewedCompanyIds.add(service.company_id);
                  }
                } else {
                  // User has already reviewed this service, track the company
                  if (service.company_id) {
                    reviewedCompanyIds.add(service.company_id);
                  }
                }
              } catch (e) {
                console.error('Failed to load reviews for service', service.id, e);
                // Still add the service if we can't check reviews (better to show than hide)
                const serviceWithReview = {
                  ...service,
                  bike: { id: bike.id, brand: bike.brand, model: bike.model },
                  company_name: service.company_name || 'Service Provider'
                };
                servicesNeedingReview.push(serviceWithReview);
              }
            }
          }
        }
      }
      
      // Sort by date (most recent first) and show only 1 at a time
      servicesNeedingReview.sort((a, b) => {
        const dateA = new Date(a.service_date);
        const dateB = new Date(b.service_date);
        return dateB - dateA; // Most recent first
      });
      
      setPendingReviews(servicesNeedingReview.slice(0, 1)); // Show only 1 at a time
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestConnection = async (companyId) => {
    try {
      await userCompanyAPI.requestConnection(companyId);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send request');
    }
  };

  const getConnectionForCompany = (companyId) => {
    return userConnections.find(c => c.company_id === companyId);
  };

  const [disconnectingCompanyId, setDisconnectingCompanyId] = useState(null);
  const handleDisconnectFromCompany = async (connectionId) => {
    if (!window.confirm('Disconnect from this service shop? They will no longer have access to your bike data.')) return;
    try {
      setDisconnectingCompanyId(connectionId);
      setError('');
      await userCompanyAPI.disconnectFromCompany(connectionId);
      setSuccess('Disconnected from shop successfully.');
      setTimeout(() => setSuccess(''), 3000);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disconnect');
    } finally {
      setDisconnectingCompanyId(null);
    }
  };

  // Handle bike selection and scroll/highlight
  const handleBikeSelect = useCallback((bikeId) => {
    if (!bikeId) {
      setHighlightedBikeId(null);
      setSelectedBikeFilter('');
      return;
    }
    
    const id = parseInt(bikeId);
    setSelectedBikeFilter(bikeId);
    setHighlightedBikeId(id);
    setShowAllBikes(true); // Show all bikes when selecting
    
    // Scroll to the bike card after a short delay to ensure it's rendered
    setTimeout(() => {
      const bikeElement = bikeRefs.current[id];
      if (bikeElement) {
        bikeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
    
    // Remove highlight after 3 seconds
    setTimeout(() => {
      setHighlightedBikeId(null);
    }, 3000);
  }, []);

  // Calculate stats
  const totalServices = bikes.reduce((acc, bike) => acc + (bike.service_count || 0), 0);
  const connectedCompanyIds = userConnections.filter(c => c.status === 'accepted').map(c => c.company_id);
  const connectedCompaniesCount = connectedCompanyIds.length;
  const displayedBikes = showAllBikes ? bikes : bikes.slice(0, 3);
  
  // Only show service shops the user is connected to
  const companiesConnected = availableCompanies.filter(c => connectedCompanyIds.includes(c.id));
  // Filter by search query within connected shops
  const filteredCompanies = companySearchQuery
    ? companiesConnected.filter(c =>
        c.name?.toLowerCase().includes(companySearchQuery.toLowerCase()) ||
        c.email?.toLowerCase().includes(companySearchQuery.toLowerCase()) ||
        (typeof c.address === 'object' && c.address?.city?.toLowerCase().includes(companySearchQuery.toLowerCase()))
      )
    : companiesConnected;
  const displayedCompanies = showAllCompanies ? filteredCompanies : filteredCompanies.slice(0, 4);

  // Review handlers
  const handleDismissReview = (serviceId) => {
    const updated = [...dismissedReviews, serviceId];
    setDismissedReviews(updated);
    localStorage.setItem('dismissedReviews', JSON.stringify(updated));
    setPendingReviews(prev => prev.filter(s => s.id !== serviceId));
  };

  const handleOpenReviewModal = (service) => {
    setReviewingService(service);
    setReviewRating(0);
    setReviewComment('');
    setShowReviewModal(true);
  };

  const handleConfirmServiceVerification = async (serviceId, accept) => {
    try {
      setVerifyingServiceId(serviceId);
      await userAPI.confirmServiceFromCompany(serviceId, accept);
      setPendingServiceVerifications(prev => prev.filter(v => v.service_id !== serviceId));
      if (accept) setSuccess('Connected to service shop successfully');
      else setSuccess('Support ticket created. We will review.');
      setTimeout(() => setSuccess(''), 4000);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process');
    } finally {
      setVerifyingServiceId(null);
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewingService || reviewRating === 0) return;
    
    try {
      setSubmittingReview(true);
      await reviewAPI.createForService(reviewingService.id, {
        rating: reviewRating,
        comment: reviewComment
      });
      setShowReviewModal(false);
      setPendingReviews(prev => prev.filter(s => s.id !== reviewingService.id));
      setReviewingService(null);
      setReviewRating(0);
      setReviewComment('');
      setSuccess('Review submitted successfully!');
      setTimeout(() => setSuccess(''), 3000);
      // Refresh data to update the list
      setTimeout(() => {
        fetchData();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <Layout
      user={user}
      onLogout={onLogout}
      pageTitle={`Welcome, ${user?.firstname || user?.username}!`}
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
      pageSubtitle="Your bike service command center"
    >
      <div className="dashboard-shell">
        {error && <div className="error" style={{ marginBottom: '20px' }}>{error}</div>}
        {success && <div className="success" style={{ marginBottom: '20px' }}>{success}</div>}

        {/* Onboarding Call Prompt - for new users within first week */}
        {showOnboardingPrompt && (
          <div style={{
            marginBottom: '24px',
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.15) 0%, rgba(74, 140, 24, 0.15) 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(61, 90, 32, 0.3)',
            position: 'relative'
          }}>
            <button
              onClick={() => {
                localStorage.setItem('dismissedOnboarding', 'true');
                setShowOnboardingPrompt(false);
              }}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'transparent',
                border: 'none',
                color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '4px 8px',
                borderRadius: '6px'
              }}
              title="Dismiss"
            >
              ×
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                background: 'rgba(74, 140, 24, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Icon name="calendar" color="brand" darkMode={darkMode} size={22} />
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <h3 style={{ 
                  margin: '0 0 6px 0', 
                  color: darkMode ? '#4A8C18' : '#3d5a20',
                  fontSize: '16px',
                  fontWeight: '700'
                }}>
                  Got questions? Book an onboarding call!
                </h3>
                <p style={{ 
                  margin: 0, 
                  color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                  fontSize: '13px'
                }}>
                  Get a free 30-minute walkthrough with our team to help you get started.
                </p>
              </div>
              <a 
                href={ONBOARDING_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 18px',
                  background: 'var(--lime-primary)',
                  color: '#ffffff',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '13px',
                  whiteSpace: 'nowrap'
                }}
              >
                <Icon name="calendar" customColor="#ffffff" size={14} />
                Book call
              </a>
            </div>
          </div>
        )}

        {/* In-app notifications (e.g. bike transfer received) */}
        {notifications.filter(n => !n.is_read).length > 0 && (
          <div style={{
            background: darkMode ? 'rgba(74, 140, 24, 0.12)' : 'rgba(74, 140, 24, 0.08)',
            border: '1px solid rgba(74, 140, 24, 0.3)',
            borderRadius: '16px',
            padding: '16px 20px',
            marginBottom: '24px'
          }}>
            {notifications.filter(n => !n.is_read).slice(0, 3).map((n) => (
              <div
                key={n.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: n.data?.bike_id ? '12px' : '8px 0',
                  borderBottom: notifications.filter(x => !x.is_read).indexOf(n) < Math.min(3, notifications.filter(x => !x.is_read).length) - 1 ? '1px solid rgba(74, 140, 24, 0.2)' : 'none'
                }}
              >
                <Icon name="bike" color="primary" darkMode={darkMode} size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: darkMode ? '#ffffff' : '#1a1a1a', fontSize: '14px' }}>{n.title}</div>
                  <div style={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)', fontSize: '13px', marginTop: '2px' }}>{n.message}</div>
                  {n.data?.bike_id && (
                    <Link
                      to={`/bike/${n.data.bike_id}`}
                      onClick={async () => {
                        try { await userAPI.markNotificationRead(n.id); } catch (_) {}
                        setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
                      }}
                      style={{ display: 'inline-block', marginTop: '8px', color: '#4A8C18', fontWeight: '600', fontSize: '13px' }}
                    >
                      View bike passport →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Review Notification */}
        {pendingReviews.length > 0 && (
          <div style={{
            background: darkMode 
              ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.12) 0%, rgba(245, 158, 11, 0.06) 100%)'
              : 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)',
            border: '1px solid rgba(251, 191, 36, 0.25)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: '200px' }}>
              <Icon name="star" customColor="#f59e0b" size={32} />
              <div>
                <div style={{ color: darkMode ? '#ffffff' : '#1a1a1a', fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>
                  Were you satisfied with the service?
                </div>
                <div style={{ color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', fontSize: '13px' }}>
                  {pendingReviews[0].company_name} • {pendingReviews[0].bike?.brand} {pendingReviews[0].bike?.model}
                  {pendingReviews[0].service_date && (
                    <span> • {new Date(pendingReviews[0].service_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => handleOpenReviewModal(pendingReviews[0])}
                className="btn btn-primary"
                style={{ padding: '10px 20px', fontSize: '13px' }}
              >
                ✍️ Yes, leave a review
              </button>
              <button
                onClick={() => handleDismissReview(pendingReviews[0].id)}
                className="btn btn-secondary"
                style={{ padding: '10px 16px', fontSize: '13px', opacity: 0.8 }}
              >
                No
              </button>
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <StatCard
            value={bikes.length}
            label="Bike Passports"
            icon={<Icon name="bikePassports" color="brand" darkMode={darkMode} size={24} />}
            color="primary"
            delay={0}
            onClick={() => navigate('/my-bikes')}
          />
          <StatCard
            value={totalServices}
            label="Total Services"
            icon={<Icon name="service" customColor="#4A8C18" size={28} />}
            color="success"
            delay={0.1}
            onClick={() => bikes.length > 0 && navigate(`/bike/${bikes[0].id}`)}
          />
          <StatCard
            value={connectedCompaniesCount}
            label="Service Shops"
            icon={<Icon name="companies" customColor="#4ecdc4" size={28} />}
            color="info"
            delay={0.2}
            onClick={() => navigate('/companies')}
          />
        </div>

        {/* Upcoming Services */}
        {upcomingBookings.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.1) 100%)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '24px',
            padding: '24px',
            marginBottom: '28px'
          }}>
            <h3 style={{
              fontSize: '18px',
              color: '#fbbf24',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <Icon name="calendar" color="secondary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              Next upcoming service
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {upcomingBookings.map((booking) => {
                const bookingDate = new Date(booking.booking_date);
                const isToday = booking.booking_date === new Date().toISOString().split('T')[0];
                return (
                  <Link
                    key={booking.id}
                    to={`/bike/${booking.bike?.id || ''}`}
                    style={{
                      background: 'var(--bg-overlay-medium)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '12px',
                      padding: '16px',
                      textDecoration: 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                    }}
                  >
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', marginBottom: '4px' }}>
                        {booking.bike?.brand} {booking.bike?.model}
                      </div>
                      <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
                        {booking.service_template?.name || 'Service'}
                        {booking.company?.name && ` @ ${booking.company.name}`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: isToday ? '#3d5a20' : 'white',
                        marginBottom: '4px'
                      }}>
                        {isToday ? 'Today' : bookingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                      {booking.start_time && (
                        <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
                          {booking.start_time.substring(0, 5)}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Actions - Prominent */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(233, 255, 183, 0.16) 0%, rgba(61, 90, 32, 0.08) 100%)',
          border: '1px solid rgba(241, 255, 217, 0.3)',
          borderRadius: '28px',
          padding: '32px',
          marginBottom: '36px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          boxShadow: 'var(--shadow-2xl)'
        }}>
        <div style={{marginBottom: '16px'}}>
          <Icon name="bike" color="brand" darkMode={darkMode} size={48} />
        </div>
        <h2 style={{
          fontSize: '24px',
          color: '#ffffff',
          marginBottom: '12px',
          fontWeight: '600'
        }}>
          Ready to service your bike?
        </h2>
        <p style={{
          color: 'rgba(255, 255, 255, 0.6)',
          marginBottom: '24px',
          fontSize: '15px'
        }}>
          Book a service appointment or find a new service shop
        </p>
        <div style={{
          display: 'flex',
          gap: '16px',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          <Link to="/book" className="btn btn-primary" style={{
            textDecoration: 'none',
            fontSize: '16px',
            padding: '14px 28px'
          }}>
            <Icon name="calendar" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Book Service Now
          </Link>
          <Link to="/companies" className="btn btn-secondary" style={{
            textDecoration: 'none',
            fontSize: '16px',
            padding: '14px 28px'
          }}>
            <Icon name="view" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Find Service Shop
          </Link>
        </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '28px',
          marginBottom: '24px'
        }}>
        {/* My Bikes - Compact */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h2 style={{
              fontSize: '20px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              margin: 0
            }}>
              <span>
                <Icon name="bikePassports" color="brand" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              </span> My Bike Passports
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {bikes.length > 1 && (
                <select
                  value={selectedBikeFilter}
                  onChange={(e) => handleBikeSelect(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'var(--bg-overlay-medium)',
                    color: '#ffffff',
                    fontSize: '13px',
                    cursor: 'pointer',
                    minWidth: '180px'
                  }}
                >
                  <option value="">Go to bike...</option>
                  {bikes.map(bike => (
                    <option key={bike.id} value={bike.id} style={{ background: '#1a1a3e' }}>
                      {bike.brand} {bike.model}
                    </option>
                  ))}
                </select>
              )}
              {bikes.length > 3 && (
                <button
                  onClick={() => setShowAllBikes(!showAllBikes)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--lime-primary)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {showAllBikes ? 'Show Less' : `Show All (${bikes.length})`}
                </button>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="loading">Loading...</div>
          ) : bikes.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: 'var(--bg-overlay-light)',
              borderRadius: '20px',
              border: '1px solid rgba(241, 255, 217, 0.2)'
            }}>
              <div style={{fontSize: '48px', marginBottom: '12px', opacity: 0.3}}>🚲</div>
              <p style={{color: 'rgba(255, 255, 255, 0.5)', margin: 0, fontSize: '14px'}}>
                No bikes yet. Add one after your first service.
              </p>
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
              {displayedBikes.map((bike) => {
                const isHighlighted = highlightedBikeId === bike.id;
                return (
                  <Link
                    key={bike.id}
                    ref={(el) => bikeRefs.current[bike.id] = el}
                    to={`/bike/${bike.id}`}
                    style={{
                      background: isHighlighted 
                        ? 'rgba(61, 90, 32, 0.15)' 
                        : 'rgba(255, 255, 255, 0.03)',
                      border: isHighlighted 
                        ? '2px solid #3d5a20' 
                        : '1px solid rgba(255, 255, 255, 0.06)',
                      borderRadius: '16px',
                      padding: '20px',
                      transition: 'all 0.3s ease',
                      textDecoration: 'none',
                      display: 'block',
                      boxShadow: isHighlighted 
                        ? '0 0 20px rgba(61, 90, 32, 0.4), 0 0 40px rgba(61, 90, 32, 0.2)' 
                        : 'none',
                      animation: isHighlighted ? 'bikeHighlightPulse 1.5s ease-in-out infinite' : 'none'
                    }}
                    onMouseOver={(e) => {
                      if (!isHighlighted) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isHighlighted) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.06)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: isHighlighted ? '#3d5a20' : 'white',
                          marginBottom: '4px'
                        }}>
                          {bike.brand} {bike.model}
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: isHighlighted ? 'rgba(61, 90, 32, 0.7)' : 'rgba(255, 255, 255, 0.5)'
                        }}>
                          {bike.year} • {bike.service_count || 0} services
                        </div>
                      </div>
                      <div style={{
                        fontSize: '24px',
                        opacity: isHighlighted ? 1 : 0.5,
                        transform: isHighlighted ? 'scale(1.2)' : 'scale(1)',
                        transition: 'all 0.3s ease'
                      }}>
                        🚲
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Service Shops - Compact */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h2 style={{
              fontSize: '20px',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              margin: 0
            }}>
              <Icon name="companies" color="info" darkMode={darkMode} size={22} /> Service Shops
            </h2>
            {filteredCompanies.length > 4 && (
              <button
                onClick={() => setShowAllCompanies(!showAllCompanies)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--lime-primary)',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {showAllCompanies ? 'Show Less' : `Show All (${filteredCompanies.length})`}
              </button>
            )}
          </div>
          
          {/* Search input */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ position: 'relative' }}>
              <Icon 
                name="search" 
                color="muted" 
                darkMode={darkMode} 
                size={18} 
                style={{ 
                  position: 'absolute', 
                  left: '14px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none'
                }} 
              />
              <input
                type="text"
                placeholder="Search service shops..."
                value={companySearchQuery}
                onChange={(e) => setCompanySearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 14px 12px 44px',
                  background: 'var(--bg-overlay-light)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
              {companySearchQuery && (
                <button
                  onClick={() => setCompanySearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    padding: '4px',
                    fontSize: '16px'
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : filteredCompanies.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: 'var(--bg-overlay-light)',
              borderRadius: '20px',
              border: '1px solid rgba(241, 255, 217, 0.2)'
            }}>
              <Icon name="companies" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p style={{color: 'rgba(255, 255, 255, 0.5)', margin: 0, fontSize: '14px'}}>
                {companySearchQuery ? 'No connected shops match your search.' : 'No connected service shops yet. Connect to shops from the Service Shops page.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {displayedCompanies.map((company) => {
                const conn = getConnectionForCompany(company.id);
                const status = conn?.status || 'none';
                
                return (
                  <div
                    key={company.id}
                    style={{
                      background: 'var(--bg-overlay-light)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      borderRadius: '16px',
                      padding: '20px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: '150px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          background: 'rgba(0,0,0,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <img src={company.logo_url || DEFAULT_APP_LOGO} alt={`${company.name} logo`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </div>
                        <div>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#ffffff',
                            marginBottom: '4px'
                          }}>
                            {company.name}
                          </div>
                          <div style={{
                            fontSize: '13px',
                            color: 'rgba(255, 255, 255, 0.5)'
                          }}>
                            {company.email || 'No contact info'}
                          </div>
                        </div>
                      </div>

                      {status === 'accepted' ? (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{
                            padding: '6px 12px',
                            background: 'rgba(74, 140, 24, 0.25)',
                            color: '#86efac',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            <Icon name="success" color="success" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                          Connected
                          </span>
                          <Link
                            to={`/companies/${company.id}`}
                            className="btn btn-sm btn-secondary"
                            style={{ 
                              padding: '6px 12px',
                              fontSize: '12px',
                              textDecoration: 'none',
                              display: 'inline-block'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Icon name="view" color="secondary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            View/Edit
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDisconnectFromCompany(conn.id)}
                            disabled={disconnectingCompanyId === conn.id}
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              background: 'rgba(239, 68, 68, 0.15)',
                              color: '#ef4444',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              borderRadius: '8px',
                              cursor: disconnectingCompanyId === conn.id ? 'wait' : 'pointer',
                              fontWeight: 500,
                            }}
                          >
                            {disconnectingCompanyId === conn.id ? '…' : 'Disconnect'}
                          </button>
                        </div>
                      ) : status === 'pending' ? (
                        <span style={{
                          padding: '6px 12px',
                          background: 'rgba(245, 158, 11, 0.2)',
                          color: '#f59e0b',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          <Icon name="loading" color="warning" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                          Pending
                        </span>
                      ) : (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleRequestConnection(company.id)}
                        >
                          Connect
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Service Verification Modal - user not connected to company that serviced their bike */}
      {pendingServiceVerifications.length > 0 && (() => {
        const v = pendingServiceVerifications[0];
        return (
          <div
            className="modal-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: darkMode ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001,
              padding: '20px'
            }}
          >
            <div style={{
              background: darkMode ? '#1a1a3e' : '#ffffff',
              borderRadius: '20px',
              maxWidth: '480px',
              width: '100%',
              padding: '28px',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <Icon name="service" customColor="#3b82f6" size={40} style={{ marginBottom: '12px' }} />
                <h2 style={{ color: darkMode ? '#ffffff' : '#1a1a1a', margin: '0 0 8px 0', fontSize: '18px' }}>
                  Service confirmation
                </h2>
                <p style={{ color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', margin: 0, fontSize: '14px', lineHeight: 1.5 }}>
                  <strong>{v.company_name}</strong> serviced your bike <strong>{v.bike_info}</strong>. You are not connected to this shop. Did they really service it?
                </p>
              </div>
              {/* Service details */}
              <div style={{
                marginBottom: '20px',
                padding: '14px 16px',
                background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                borderRadius: '12px',
                border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                textAlign: 'left',
                maxHeight: '180px',
                overflowY: 'auto',
              }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Service details
                </div>
                {v.service_date && (
                  <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', marginBottom: '8px' }}>
                    {new Date(v.service_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    {v.service_time && (
                      <span style={{ marginLeft: '6px' }}>
                        {String(v.service_time).slice(0, 5)}
                      </span>
                    )}
                  </div>
                )}
                {v.description && (
                  <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)', marginBottom: '10px', lineHeight: 1.5 }}>
                    {v.description}
                  </div>
                )}
                {v.items && v.items.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    {v.items.map((it, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)', padding: '4px 0', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' }}>
                        <span>{(it.quantity || 1)}x {it.item_name}</span>
                        <span style={{ fontWeight: 500 }}>£{(it.total_price || it.unit_price || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {v.total_cost > 0 && (
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#22c55e', marginTop: '8px', paddingTop: '8px', borderTop: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)' }}>
                    Total: £{parseFloat(v.total_cost).toFixed(2)}
                  </div>
                )}
                {!v.description && (!v.items || v.items.length === 0) && (!v.total_cost || v.total_cost === 0) && (
                  <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)', fontStyle: 'italic' }}>
                    No service details recorded.
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleConfirmServiceVerification(v.service_id, true)}
                  disabled={verifyingServiceId === v.service_id}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontWeight: 600,
                    border: 'none',
                    cursor: verifyingServiceId === v.service_id ? 'not-allowed' : 'pointer',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                >
                  {verifyingServiceId === v.service_id ? 'Processing...' : 'Yes, add connection'}
                </button>
                <button
                  onClick={() => handleConfirmServiceVerification(v.service_id, false)}
                  disabled={verifyingServiceId === v.service_id}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '10px',
                    fontWeight: 600,
                    border: '2px solid #ef4444',
                    cursor: verifyingServiceId === v.service_id ? 'not-allowed' : 'pointer',
                    background: 'transparent',
                    color: '#ef4444',
                    fontSize: '14px'
                  }}
                >
                  No, create support ticket
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Review Modal */}
      {showReviewModal && reviewingService && (
        <div 
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowReviewModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: darkMode ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div style={{
            background: darkMode ? '#1a1a3e' : '#ffffff',
            borderRadius: '20px',
            maxWidth: '450px',
            width: '100%',
            padding: '28px',
            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Icon name="star" customColor="#f59e0b" size={48} style={{ marginBottom: '12px' }} />
              <h2 style={{ color: darkMode ? '#ffffff' : '#1a1a1a', margin: '0 0 8px 0', fontSize: '20px' }}>
                Rate your service
              </h2>
              <p style={{ color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)', margin: 0, fontSize: '14px' }}>
                {reviewingService.company_name} • {reviewingService.bike?.brand} {reviewingService.bike?.model}
              </p>
            </div>

            {/* Star Rating */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewRating(star)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '56px',
                      cursor: 'pointer',
                      color: star <= reviewRating ? '#fbbf24' : (darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'),
                      transition: 'transform 0.15s, color 0.15s',
                      transform: star <= reviewRating ? 'scale(1.15)' : 'scale(1)',
                      padding: '6px'
                    }}
                  >
                    ★
                  </button>
                ))}
              </div>
              {reviewRating > 0 && (
                <div style={{ fontSize: '14px', color: '#fbbf24', fontWeight: '500' }}>
                  {reviewRating === 1 && 'Very poor'}
                  {reviewRating === 2 && 'Poor'}
                  {reviewRating === 3 && 'Average'}
                  {reviewRating === 4 && 'Good'}
                  {reviewRating === 5 && 'Excellent!'}
                </div>
              )}
            </div>

            {/* Comment */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', fontSize: '13px' }}>
                Note (optional)
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Describe your experience..."
                rows="3"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.2)',
                  background: darkMode ? 'rgba(0,0,0,0.3)' : '#ffffff',
                  color: darkMode ? '#ffffff' : '#1a1a1a',
                  fontSize: '14px',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSubmitReview}
                className="btn btn-primary"
                disabled={reviewRating === 0 || submittingReview}
                style={{ flex: 1, padding: '14px' }}
              >
                {submittingReview ? (
                  <>
                    <Icon name="loading" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Icon name="success" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Submit review
                  </>
                )}
              </button>
              <button
                onClick={() => setShowReviewModal(false)}
                className="btn btn-secondary"
                style={{ padding: '14px 20px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default UserDashboard;
